import React, { useState, useRef, useEffect } from 'react';
import { Bell, X } from 'lucide-react';

function timeAgo(d) {
  if (!d) return '';
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Shared bell + dropdown for all three navbars (admin/filmmaker/reviewer).
// The caller owns the actual data (useNotifications hook) and click-through
// behavior (onItemClick) since admin needs to open a ReviewDrawer tab while
// filmmaker/reviewer just navigate to their dashboard.
export default function NotificationBell({ unreadCount, items, loading, fetchList, markRead, markAllRead, deleteOne, clearAll, onItemClick, size = 32 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList(1);
  };

  const handleItemClick = (n) => {
    if (!n.is_read) markRead(n.id);
    setOpen(false);
    onItemClick?.(n);
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button onClick={toggle} title="Notifications" style={{ ...s.btn, width: size, height: size }}>
        <Bell size={15} color="var(--text-secondary)" />
        {unreadCount > 0 && (
          <span style={s.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div style={s.dropdown} className="fade-in">
          <div style={s.header}>
            <span style={s.headerTitle}>Notifications</span>
            <div style={{ display: 'flex', gap: 10 }}>
              {unreadCount > 0 && <button style={s.markAllBtn} onClick={markAllRead}>Mark all read</button>}
              {items.length > 0 && <button style={{ ...s.markAllBtn, color: 'var(--text-muted)' }} onClick={clearAll}>Clear all</button>}
            </div>
          </div>
          <hr className="divider" />
          <div style={s.list}>
            {loading && <div style={s.empty}>Loading…</div>}
            {!loading && items.length === 0 && <div style={s.empty}>No notifications yet.</div>}
            {!loading && items.map(n => (
              <div key={n.id} style={{ ...s.item, ...(n.is_read ? {} : s.itemUnread), display: 'flex', alignItems: 'flex-start', gap: 6, cursor: 'default' }}>
                <button onClick={() => handleItemClick(n)} style={s.itemMain}>
                  <div style={{ ...s.itemTitle, fontWeight: n.is_read ? 600 : 800 }}>{n.title}</div>
                  {n.body && <div style={s.itemBody}>{n.body}</div>}
                  <div style={s.itemTime}>{timeAgo(n.created_at)}</div>
                </button>
                <button onClick={() => deleteOne(n.id)} title="Clear this notification" style={s.itemClearBtn}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  btn: { position: 'relative', borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  badge: { position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16, padding: '0 3px', borderRadius: 8, background: 'var(--pink)', color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px var(--bg-card)' },
  dropdown: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 320, maxHeight: 420, display: 'flex', flexDirection: 'column', background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflow: 'hidden', zIndex: 300 },
  header: { padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerTitle: { fontSize: 13, fontWeight: 800, color: 'var(--text)' },
  markAllBtn: { background: 'none', border: 'none', color: 'var(--pink)', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)' },
  list: { overflowY: 'auto', flex: 1 },
  empty: { padding: '20px 16px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' },
  item: { width: '100%', padding: '10px 8px 10px 16px', background: 'none', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font)' },
  itemMain: { flex: 1, minWidth: 0, textAlign: 'left', display: 'block', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', padding: 0 },
  itemUnread: { background: 'var(--pink-glow)' },
  itemTitle: { fontSize: 12, color: 'var(--text)', marginBottom: 2 },
  itemBody: { fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2, lineHeight: 1.4 },
  itemTime: { fontSize: 10, color: 'var(--text-muted)' },
  itemClearBtn: { flexShrink: 0, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--r-sm)' },
};
